// Section 24.5: the one modifier pipeline. Every computed number is a base plus labelled
// modifiers; `evaluate` is the only place they are combined, and its factor list is what
// explain() returns. Contributors are registered functions called in a fixed order.
import type {
  Evaluation,
  Factor,
  Modifier,
  ModifierOp,
  ReadContext,
  Target,
  TargetKind,
} from './types';

/**
 * The fixed contributor order (24.5), extended with `illnesses` and `jobs` so every content
 * registry has a slot (ADR-0010). Order affects only the readability of factor lists.
 */
export const CONTRIBUTOR_ORDER = [
  'season',
  'land',
  'needs',
  'age',
  'illnesses',
  'traits',
  'skills',
  'tools',
  'jobs',
  'buildings',
  'policies',
  'norms',
  'events',
] as const;

export type ContributorSlot = (typeof CONTRIBUTOR_ORDER)[number];

/** Every Target kind, for indexing contributors. Kept exhaustive by the type check below. */
export const TARGET_KINDS = [
  'output',
  'jobRisk',
  'demand',
  'illnessRisk',
  'illnessDeath',
  'catchRisk',
  'illnessMultiplier',
  'healthGain',
  'healthLoss',
  'strengthChange',
  'strengthCap',
  'crimeRisk',
  'happinessTarget',
  'trustChange',
  'skillGain',
  'emigrationPressure',
  'elderDeath',
  'conception',
  'buildCost',
  'buildingEffect',
  'spoilage',
  'capacity',
  'service',
  'deterrence',
  'harvestYield',
  'eventWeight',
] as const satisfies readonly TargetKind[];

// Compile-time check that TARGET_KINDS covers the whole Target union.
type MissingKinds = Exclude<TargetKind, (typeof TARGET_KINDS)[number]>;
export const TARGET_KINDS_COMPLETE: MissingKinds extends never ? true : MissingKinds = true;

/**
 * Optional narrowing (Section 2 budget, M6): for a target kind listed here, the contributor is
 * asked only about targets whose sub-key (`subKeyOf`: the job, resource or cause) is listed; it
 * must return [] for every other target of that kind. Unlisted kinds are not narrowed.
 */
export type ModifierKeys = Partial<Record<TargetKind, readonly string[]>>;

/** The key that narrows a target kind's contributors: the job, the resource or the cause. */
export function subKeyOf(target: Target): string | null {
  switch (target.kind) {
    case 'output':
    case 'jobRisk':
      return target.jobId;
    case 'demand':
      return target.resource;
    case 'trustChange':
    case 'healthLoss':
      return target.cause;
    default:
      return null;
  }
}

export interface Contributor {
  slot: ContributorSlot;
  /** Namespaced id used in errors and tests, e.g. 'trait:hardy'. */
  id: string;
  /** Target kinds this contributor may affect; it is not called for others. */
  targets: readonly TargetKind[];
  /** Narrower still, by sub-key (see ModifierKeys). */
  only?: ModifierKeys;
  /** Pure: reads ctx.state and ctx.cfg only; returns [] for targets it does not affect. */
  modifiers(ctx: ReadContext, target: Target): readonly Modifier[];
}

export interface ModifierPipeline {
  readonly byKind: Readonly<Record<TargetKind, readonly Contributor[]>>;
  /** byKind narrowed by sub-key, filled on first use (same order as byKind). */
  readonly bySubKey: ReadonlyMap<TargetKind, Map<string, readonly Contributor[]>>;
}

/** Build the per-kind index, stable-sorted by slot so factor lists are deterministic. */
export function createPipeline(contributors: readonly Contributor[]): ModifierPipeline {
  const slotRank = new Map<ContributorSlot, number>(CONTRIBUTOR_ORDER.map((slot, i) => [slot, i]));
  const ordered = contributors
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (slotRank.get(a.c.slot) ?? 0) - (slotRank.get(b.c.slot) ?? 0) || a.i - b.i)
    .map((x) => x.c);
  const byKind = {} as Record<TargetKind, Contributor[]>;
  for (const kind of TARGET_KINDS) byKind[kind] = [];
  for (const c of ordered) for (const kind of c.targets) byKind[kind].push(c);
  const bySubKey = new Map<TargetKind, Map<string, readonly Contributor[]>>();
  for (const kind of TARGET_KINDS)
    if (byKind[kind].some((c) => c.only?.[kind] !== undefined)) bySubKey.set(kind, new Map());
  return { byKind, bySubKey };
}

/** The contributors to ask about `target`, in slot order (narrowed by sub-key where declared). */
function contributorsFor(pipeline: ModifierPipeline, target: Target): readonly Contributor[] {
  const all = pipeline.byKind[target.kind];
  const narrowed = pipeline.bySubKey.get(target.kind);
  if (narrowed === undefined) return all;
  const key = subKeyOf(target);
  if (key === null) return all;
  let list = narrowed.get(key);
  if (list === undefined) {
    list = all.filter((c) => {
      const keys = c.only?.[target.kind];
      return keys === undefined || keys.includes(key);
    });
    narrowed.set(key, list);
  }
  return list;
}

/** Ask every registered contributor for `target`, in the fixed order, and concatenate. */
export function collect(ctx: ReadContext, target: Target): Modifier[] {
  const out: Modifier[] = [];
  const contributors = contributorsFor(ctx.registry.pipeline, target);
  for (let i = 0; i < contributors.length; i++) {
    const mods = (contributors[i] as Contributor).modifiers(ctx, target);
    for (let j = 0; j < mods.length; j++) out.push(mods[j] as Modifier);
  }
  return out;
}

const OP_ORDER: readonly ModifierOp[] = ['set', 'add', 'mul', 'min', 'max'];

function factor(m: Modifier, delta: number): Factor {
  const row: Factor = { source: m.source, op: m.op, value: m.value, delta };
  if (m.note !== undefined) row.note = m.note;
  return row;
}

/**
 * Combine a base with modifiers: all `set` (last wins) -> all `add` -> all `mul` -> all `min`
 * caps -> all `max` floors. Within an op class the listed order is kept for the factor rows;
 * the resulting value is independent of order within a class.
 */
export function evaluate(base: number, mods: readonly Modifier[]): Evaluation {
  let value = base;
  const factors: Factor[] = [{ source: 'base', op: 'base', value: base, delta: base }];
  for (const op of OP_ORDER) {
    for (const m of mods) {
      if (m.op !== op) continue;
      const before = value;
      switch (op) {
        case 'set':
          value = m.value;
          break;
        case 'add':
          value = value + m.value;
          break;
        case 'mul':
          value = value * m.value;
          break;
        case 'min':
          value = Math.min(value, m.value);
          break;
        case 'max':
          value = Math.max(value, m.value);
          break;
      }
      factors.push(factor(m, value - before));
    }
  }
  return { value, factors };
}

/**
 * `evaluate(base, mods).value` without building the factor rows (Section 2 budget): the same
 * operations in the same order, so the number is identical. `num()` uses it; explain() and
 * anything that shows factors uses `evaluate`.
 */
export function evaluateValue(base: number, mods: readonly Modifier[]): number {
  let value = base;
  const n = mods.length;
  if (n === 0) return value;
  for (const op of OP_ORDER) {
    for (let i = 0; i < n; i++) {
      const m = mods[i] as Modifier;
      if (m.op !== op) continue;
      switch (op) {
        case 'set':
          value = m.value;
          break;
        case 'add':
          value = value + m.value;
          break;
        case 'mul':
          value = value * m.value;
          break;
        case 'min':
          value = Math.min(value, m.value);
          break;
        case 'max':
          value = Math.max(value, m.value);
          break;
      }
    }
  }
  return value;
}

/** Convenience constructors so content reads like the spec tables. */
export const mod = {
  set: (source: string, value: number, note?: string): Modifier => make(source, 'set', value, note),
  add: (source: string, value: number, note?: string): Modifier => make(source, 'add', value, note),
  mul: (source: string, value: number, note?: string): Modifier => make(source, 'mul', value, note),
  min: (source: string, value: number, note?: string): Modifier => make(source, 'min', value, note),
  max: (source: string, value: number, note?: string): Modifier => make(source, 'max', value, note),
};

function make(source: string, op: ModifierOp, value: number, note: string | undefined): Modifier {
  const m: Modifier = { source, op, value };
  if (note !== undefined) m.note = note;
  return m;
}
